import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { Mail, Phone, ShieldCheck } from "lucide-react";
import { contact } from "@/lib/healic-data";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Healic" },
      {
        name: "description",
        content:
          "Read Healic's Privacy Policy to understand how we collect, use, retain, and protect your personal and medical information.",
      },
      { property: "og:title", content: "Privacy Policy — Healic" },
      {
        property: "og:description",
        content:
          "How Healic collects, uses, retains, and protects your personal and medical information.",
      },
      { property: "og:url", content: `${SITE_URL}/privacy-policy` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/privacy-policy` }],
  }),
  component: PrivacyPolicyPage,
});

const sections = [
  {
    id: "scope",
    title: "Scope of this Privacy Policy",
    content: [
      "This Privacy Policy applies to all Healic digital platforms, including:",
    ],
    bullets: [
      "Healic Customer App — Used by patients and customers for appointments, services, prescriptions, reports, invoices, health records, and other healthcare-related services.",
      "Healic Doctor App — Used by doctors and medical professionals to manage appointments, consultations, prescriptions, patient notes, and related medical workflows.",
      "Healic Backoffice App — Used by Healic staff and authorized team members for patient management, appointment handling, billing, reporting, service management, and operational activities.",
      "Healic Website — Used for providing information about Healic, services, doctors, departments, contact options, and related communication.",
    ],
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    content: [
      "We may collect information from users when they use our website, Customer App, Doctor App, or Backoffice App. This information may include:",
    ],
    bullets: [
      "Name",
      "Mobile number",
      "Email address",
      "Age, gender, and basic profile details",
      "Appointment details",
      "Doctor consultation details",
      "Prescriptions",
      "Medical reports",
      "Billing and invoice details",
      "Service history",
      "Health-related information shared during use of our services",
      "Communication records",
      "Device, browser, and usage information",
      "Any other information voluntarily provided by the user",
    ],
    afterBullets: [
      "For doctors, staff, and backoffice users, we may also collect role-based operational information required to provide access and manage internal workflows.",
    ],
  },
  {
    id: "how-we-use-information",
    title: "How We Use Information",
    content: [
      "Healic uses collected information for the following purposes:",
    ],
    bullets: [
      "To create and manage user profiles",
      "To book, manage, and track appointments",
      "To support doctor consultations",
      "To manage prescriptions, reports, and medical records",
      "To generate bills, invoices, and service records",
      "To provide customer support",
      "To improve our services, apps, and website",
      "To communicate important updates",
      "To maintain security and prevent misuse",
      "To comply with legal, medical, regulatory, tax, and operational requirements",
      "To support internal reporting and business operations",
    ],
    afterBullets: ["We do not sell users' personal or medical data to third parties."],
  },
  {
    id: "sharing-of-information",
    title: "Sharing of Information",
    content: [
      "Healic may share user information only when required for service delivery, legal compliance, or operational purposes. Information may be shared with:",
    ],
    bullets: [
      "Doctors and healthcare professionals associated with Healic",
      "Authorized Healic staff and backoffice users",
      "Diagnostic, pharmacy, billing, or service partners where required",
      "Technology service providers who help us operate our apps, website, infrastructure, or communication systems",
      "Government, legal, or regulatory authorities, when required by applicable law",
    ],
    afterBullets: [
      "We ensure that access to user information is limited to authorized users and only for legitimate purposes.",
    ],
  },
  {
    id: "data-retention",
    title: "Data Retention",
    content: [
      "Healic retains user data collected through the Customer App, Doctor App, Backoffice App, and website only for as long as required to provide healthcare services, manage appointments, maintain medical records, process billing, comply with legal obligations, resolve disputes, maintain security, and support business operations.",
      "Some data, such as medical records, appointment history, invoices, prescriptions, reports, payment records, and audit logs, may be retained for a longer period where required under applicable laws, healthcare regulations, tax rules, medical record requirements, or legitimate business purposes.",
      "When data is no longer required, Healic may delete, archive, or anonymize it in a secure manner.",
    ],
  },
  {
    id: "data-deletion",
    title: "Data Deletion",
    content: [
      "Users may request deletion of their personal data by contacting Healic at: support@healic.in",
      "Once a deletion request is received, Healic will verify the request and process it within a reasonable period.",
      "Data that is not required for legal, medical, regulatory, accounting, billing, dispute resolution, audit, security, or legitimate healthcare and operational purposes will be deleted or anonymized.",
      "Some information may continue to be retained where required by law or for legitimate healthcare and operational purposes, such as:",
    ],
    bullets: [
      "Medical records",
      "Prescriptions",
      "Diagnostic reports",
      "Appointment history",
      "Billing and invoice records",
      "Payment records",
      "Audit logs",
      "Legal or dispute-related records",
      "Fraud prevention and security records",
    ],
    afterBullets: [
      "Users may also request correction or update of their personal information by contacting us at the same email address.",
    ],
  },
  {
    id: "data-security",
    title: "Data Security",
    content: [
      "Healic takes reasonable technical and organizational measures to protect user information from unauthorized access, misuse, loss, alteration, or disclosure.",
      "Access to sensitive information is restricted to authorized doctors, staff, and system users based on their role and operational requirement.",
      "However, no digital platform, internet transmission, or electronic storage system can be guaranteed to be completely secure. Users are advised to use the apps responsibly and keep their login credentials confidential.",
    ],
  },
  {
    id: "third-party-services",
    title: "Third-Party Services",
    content: [
      "Healic may use trusted third-party services for hosting, analytics, communication, payment processing, notifications, or operational support.",
      "These third-party service providers may process limited user information only as required to provide their services to Healic.",
      "Healic does not authorize third-party service providers to use user data for their own independent marketing or unrelated purposes.",
    ],
  },
  {
    id: "cookies-and-analytics",
    title: "Cookies and Analytics",
    content: [
      "Healic's website may use cookies and analytics tools to understand website usage, improve performance, and enhance user experience.",
      "Users may choose to disable cookies through their browser settings. However, some parts of the website may not function properly if cookies are disabled.",
    ],
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    content: [
      "Healic's services may include healthcare services for minors. In such cases, information may be provided by parents, guardians, or authorized representatives.",
      "We do not knowingly collect personal information from children without appropriate consent or authorization where required.",
    ],
  },
  {
    id: "user-rights",
    title: "User Rights",
    content: ["Users may contact Healic to:"],
    bullets: [
      "Access their personal information",
      "Request correction of incorrect information",
      "Request deletion of their data",
      "Raise privacy-related concerns",
      "Withdraw consent where applicable",
    ],
    afterBullets: [
      "For any such request, users can contact us at: support@healic.in",
    ],
  },
  {
    id: "privacy-complaints",
    title: "Privacy Complaints",
    content: [
      "If you have any questions, complaints, or concerns related to this Privacy Policy or handling of your data, please contact us at: support@healic.in",
      "We will review your concern and try to respond within a reasonable period.",
    ],
  },
  {
    id: "contact-us",
    title: "Contact Us",
    content: [
      "For privacy-related queries, data deletion requests, or grievances, please contact:",
    ],
    contact: true,
  },
  {
    id: "updates",
    title: "Updates to this Privacy Policy",
    content: [
      "Healic may update this Privacy Policy from time to time. Any changes will be published on this page with the updated date.",
      "Users are encouraged to review this page periodically to stay informed about how we protect their information.",
    ],
  },
];

function PrivacyPolicyPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        <div className="relative mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" />
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              Legal
            </p>
          </div>
          <h1 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-3 max-w-3xl text-sm opacity-85 sm:text-base">
            At Healic, we respect your privacy and are committed to protecting the
            personal, medical, and operational information shared with us through our
            website, Customer App, Doctor App, and Backoffice App.
          </p>
          <p className="mt-4 text-xs opacity-70">
            Last Updated: June 06, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-4xl px-4 py-10 lg:px-8 lg:py-14">
        <p className="text-sm leading-relaxed text-muted-foreground">
          By using Healic's website or any of our apps, you agree to the practices described in this Privacy Policy.
        </p>

        <div className="mt-10 space-y-10">
          {sections.map((s) => (
            <div
              key={s.id}
              id={s.id}
              className="rounded-3xl border border-border bg-card p-6 shadow-soft lg:p-8"
            >
              <h2 className="font-display text-xl font-bold sm:text-2xl">
                {s.title}
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                {s.content.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-foreground/85">
                  {s.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
              {s.afterBullets && (
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                  {s.afterBullets.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              )}
              {s.contact && (
                <div className="mt-4 space-y-2 text-sm text-foreground/85">
                  <p className="font-semibold">Healic</p>
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex items-center gap-2 text-primary hover:text-accent"
                  >
                    <Mail className="h-4 w-4" /> {contact.email}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 rounded-3xl bg-accent-soft/40 p-6 text-center lg:p-10">
          <h3 className="font-display text-xl font-bold">Have questions about your privacy?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Reach out to our support team and we will be happy to help.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <a
              href={`mailto:${contact.email}`}
              className="inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-cta hover:opacity-90"
            >
              <Mail className="h-4 w-4" /> {contact.email}
            </a>
            <a
              href={`tel:${contact.phoneTel}`}
              className="inline-flex items-center gap-2 rounded-pill bg-card px-5 py-2.5 text-sm font-semibold text-foreground ring-1 ring-border hover:bg-muted"
            >
              <Phone className="h-4 w-4" /> {contact.phone}
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
